"use client";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import MoreAboutHotel from "./MoreAboutHotel";
import BreadCrumb from "@/components/BreadCrumb";
import AboutHotel from "@/components/pages/hotel/detail/AboutHotel";
import GallerySection from "@/components/pages/hotel/detail/GallerySection";
import Header from "@/components/pages/hotel/detail/Header";
import {
  useHotelDetailAddToCartAnalytics,
  useHotelDetailViewItemAnalytics,
} from "@/components/pages/hotel/detail/hooks";
import HotelFeatures from "@/components/pages/hotel/detail/HotelFeatures";
import HotelFeaturesSection from "@/components/pages/hotel/detail/HotelFeaturesSection";
import HotelInfoSection from "@/components/pages/hotel/detail/HotelInfoSection";
import HotelPolicies from "@/components/pages/hotel/detail/HotelPolicies";
import HotelRooms from "@/components/pages/hotel/detail/HotelRooms";
import ImportantInfo from "@/components/pages/hotel/detail/ImportantInfo";
import NearByHotels from "@/components/pages/hotel/detail/NearByHotels";
import TabNavigation from "@/components/pages/hotel/detail/TabNavigation";
import { useHotelMapViewAnalytics } from "@/components/pages/hotel/search/hooks/useHotelMapViewAnalytics";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useModalContext } from "@/contexts/ModalContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import { hotelBasicService } from "@/services/hotelServices/basic";
import { usePriceFormatter } from "@/hooks/usePriceFormatter";
import smoothScrollToElement from "@/utils/smoothScroll";

const ROOM_SEARCH_SCROLL_PARAM = "scroll_to_rooms";
const HOTEL_NOT_FOUND_MODAL_ID = "hotel-not-found-modal";

export default function HotelDetailWrapper({
  hotelName,
  checkinDate,
  checkOutDate,
  locale,
  hotelData = null,
  roomsData = [],
  currency: serverCurrency,
  error = null,
  initialBreadcrumbItems = undefined,
}) {
  const t = useTranslationDetector();
  const { currency: clientCurrency } = useCurrency();
  const currency = serverCurrency || clientCurrency;
  const { formatPrice } = usePriceFormatter();
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { openModal, closeModal } = useModalContext();

  const handleHotelNotFoundModalClose = useCallback(() => {
    closeModal(HOTEL_NOT_FOUND_MODAL_ID);
    router.back();
  }, [closeModal, router]);

  // Breadcrumb state: server'dan gelirse direkt kullanılır.
  const [breadcrumbItems, setBreadcrumbItems] = useState(
    initialBreadcrumbItems || [],
  );

  // URL parametrelerini parse et (adult_1, child_1, childage_1_1 formatı)
  // useSearchParams() ReadonlyURLSearchParams döndürüyor, direkt .get() ile parse ediyoruz
  // searchParams değiştiğinde component yeniden render olur, bu yüzden her render'da güncel değerleri alıyoruz
  const adultParam = searchParams.get("adult_1") || "1";
  const childParam = searchParams.get("child_1") || "0";

  // childage parametrelerini topla ve parse et
  const childCount = parseInt(childParam, 10) || 0;

  // childAges array'ini memoize et - sadece değerler gerçekten değiştiğinde yeni array oluştur
  // Tüm childage parametrelerini string olarak topla (dependency check için)
  // searchParams.toString() kullanarak tüm query parametrelerini string'e çeviriyoruz
  // Bu sayede URL değiştiğinde memoization düzgün çalışır
  const childAgesString = useMemo(() => {
    const ageStrings = [];
    for (let i = 1; i <= childCount; i++) {
      const age = searchParams.get(`childage_1_${i}`);
      if (age) {
        ageStrings.push(age);
      }
    }
    return ageStrings.join(",");
  }, [childCount, searchParams]);

  const childAges = useMemo(() => {
    if (!childAgesString) return [];
    return childAgesString
      .split(",")
      .map((age) => {
        const parsedAge = parseInt(age, 10);
        return !isNaN(parsedAge) ? parsedAge : null;
      })
      .filter((age) => age !== null);
  }, [childAgesString]);

  // Parse edilmiş değerleri hesapla
  const adult = parseInt(adultParam, 10) || 1;
  const child = parseInt(childParam, 10) || 0;

  // guests objesini memoize et - sadece değerler gerçekten değiştiğinde yeni obje oluştur
  // childAges array'ini string'e çevirerek dependency olarak kullan (array referans değişikliğini algılamak için)
  const guests = useMemo(
    () => ({
      adult,
      child,
      childrenAges: childAges,
    }),
    [adult, child, childAges],
  );

  // Analytics: view_item için product objesi (detay API response → buildHotelEcommerceItem uyumlu)
  const productForViewItem = useMemo(() => {
    if (!hotelData) return null;
    const details = hotelData.HotelDetails ?? {};
    const firstRoom = roomsData?.[0];
    const firstPrice = firstRoom?.RoomPrices?.[0];
    const pricing = firstPrice?.RoomPricing;
    const pricingCurrency = pricing?.Currency || "TRY";
    const netPrice =
      typeof pricing?.NetPrice === "number" ? pricing.NetPrice : null;
    const grossPrice =
      typeof pricing?.GrossPrice === "number" ? pricing.GrossPrice : null;
    const discountRate =
      typeof pricing?.DiscountRate === "number" ? pricing.DiscountRate : 0;
    // view_item: price = grossPrice, discount = grossPrice - netPrice (virgülden sonra 2 hane)
    const price = netPrice ?? grossPrice ?? 0;
    // buildHotelEcommerceItem için Address (item_category3–5, hotel_country, hotel_region, state).
    // API Address altında CountryName göndermiyor; HotelDetails.CountryName kullanılıyor (hotel_country tutarlı "Fransa" vb.)
    const countryNameValue =
      details.CountryName ??
      (hotelData.Country === "TR" ? "Türkiye" : hotelData.CountryName) ??
      null;
    const Address =
      details.Address !== null ||
      details.Address !== undefined ||
      details.City !== null ||
      details.City !== undefined ||
      details.Country !== null ||
      details.Country !== undefined ||
      details.CountryName !== null ||
      details.CountryName !== undefined ||
      hotelData.City !== null ||
      hotelData.City !== undefined ||
      hotelData.Country !== null ||
      hotelData.Country !== undefined
        ? {
            city: details.City ?? hotelData.City ?? null,
            district: details.District ?? hotelData.District ?? null,
            region:
              details.Region ??
              hotelData.Region ??
              hotelData.RegionName ??
              null,
            line: details.Address ?? hotelData.Address ?? null,
            country: countryNameValue,
            CountryName: countryNameValue,
            countryName: countryNameValue,
            state: details.State ?? hotelData.State ?? null,
          }
        : null;
    return {
      HotelId: hotelData.HotelId,
      HotelName: hotelData.HotelName,
      name: hotelData.HotelName,
      Slug: hotelData.Slug,
      location: details.Location ?? hotelData.City ?? "",
      roomTypeDescription: firstRoom?.RoomName || "default",
      RoomTypeName: firstRoom?.RoomName,
      Currency: pricingCurrency,
      PriceCurrency: pricingCurrency,
      price,
      priceOriginal: grossPrice ?? undefined,
      discountRate,
      SurveyScore: details.SurveyScore ?? hotelData.SurveyScore,
      StarNumber: details.StarNumber ?? hotelData.StarNumber,
      IsDomestic: hotelData.IsDomestic,
      isRefundable: firstPrice?.CancellationPolicy?.IsRefundable ?? null,
      Country:
        details.CountryName ??
        (hotelData.Country === "TR" ? "Türkiye" : hotelData.Country),
      CountryName:
        details.CountryName ??
        (hotelData.Country === "TR" ? "Türkiye" : hotelData.Country),
      Region: details.Region ?? hotelData.Region ?? "",
      RegionName: details.Region ?? hotelData.RegionName ?? "",
      checkIn: checkinDate || null,
      checkOut: checkOutDate || null,
      CheckinDate: checkinDate,
      CheckoutDate: checkOutDate,
      Address,
      Tags: details.Tags ?? hotelData.Tags ?? null,
      MarketType: details.MarketType ?? hotelData.MarketType ?? null,
      ProviderId: hotelData.ProviderId ?? null,
    };
  }, [hotelData, roomsData, checkinDate, checkOutDate]);

  const passengersForAnalytics = useMemo(
    () => ({
      guest_total: adult + child,
      guest_adult_number: adult,
      guest_children_number: child,
    }),
    [adult, child],
  );

  useHotelDetailViewItemAnalytics({
    product: productForViewItem,
    passengers: passengersForAnalytics,
    itemListId: "hotel_detail",
    itemListName: "Hotel Detail",
    crossProductPromotion: undefined,
    basketId:
      hotelData?.BasketId !== null &&
      hotelData?.BasketId !== undefined &&
      String(hotelData.BasketId).trim() !== ""
        ? String(hotelData.BasketId)
        : undefined,
  });

  const { trackAddToCart } = useHotelDetailAddToCartAnalytics({
    itemListId: "hotel_detail",
    itemListName: "Hotel Detail",
    basketId:
      hotelData?.BasketId !== null &&
      hotelData?.BasketId !== undefined &&
      String(hotelData.BasketId).trim() !== ""
        ? String(hotelData.BasketId)
        : undefined,
    crossProductPromotion: undefined,
  });

  const detailsForMap = hotelData?.HotelDetails ?? {};
  const isDomesticForMap = hotelData?.IsDomestic ?? hotelData?.Country === "TR";
  const { trackHotelMapView } = useHotelMapViewAnalytics({
    hotel_city: detailsForMap.City ?? hotelData?.City ?? null,
    hotel_district: detailsForMap.District ?? hotelData?.District ?? null,
    hotel_neighborhood:
      detailsForMap.Neighborhood ?? hotelData?.Neighborhood ?? null,
    hotel_country:
      detailsForMap.CountryName ??
      (hotelData?.Country === "TR"
        ? "Türkiye"
        : (hotelData?.CountryName ?? hotelData?.Country)) ??
      null,
    hotel_region:
      detailsForMap.Region ??
      hotelData?.Region ??
      hotelData?.RegionName ??
      null,
    state: isDomesticForMap
      ? null
      : (detailsForMap.State ?? hotelData?.State ?? null),
    guest_total: adult + child,
    guest_adult_number: adult,
    guest_children_number: child,
    start_date: checkinDate ?? null,
    end_date: checkOutDate ?? null,
  });

  const handleRoomAddToCart = useCallback(
    (room, selectedOption, accommodationLabel) => {
      if (!productForViewItem) return;
      trackAddToCart({
        product: productForViewItem,
        roomOption: {
          roomId: room.id,
          roomName: room.label,
          accommodationLabel: accommodationLabel ?? selectedOption?.label ?? "",
          price: selectedOption?.discountedPrice ?? selectedOption?.price ?? 0,
          discountRate: selectedOption?.discountRate ?? 0,
          refundable: selectedOption?.refundable ?? false,
          currency: productForViewItem.Currency ?? "TRY",
        },
        passengers: passengersForAnalytics,
      });
    },
    [productForViewItem, trackAddToCart, passengersForAnalytics],
  );

  const navRef = useRef(null);
  const isInitialScrollInProgress = useRef(false);

  const [showSticky, setShowSticky] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [sectionTrackingReady, setSectionTrackingReady] = useState(false);

  // Breadcrumb API çağrısı
  useEffect(() => {
    // Breadcrumb schema/HTML için client fetch'i tamamen kapatmak için:
    // initialBreadcrumbItems prop'u geliyorsa hiçbir fetch yapılmaz.
    if (initialBreadcrumbItems !== undefined) {
      return;
    }

    const fetchBreadcrumb = async () => {
      if (!hotelName) {
        return;
      }

      try {
        const languageCode = locale.toUpperCase();
        const response = await hotelBasicService.getBreadcrumb({
          ViewType: "hotel",
          Slug: hotelName,
          LanguageCode: languageCode,
        });

        if (
          response?.Items &&
          Array.isArray(response.Items) &&
          response.Items.length > 0
        ) {
          // API response'unu breadcrumb formatına çevir
          // API'den gelen tüm item'ları kullan (Turna, Otel, City, HotelName hepsi geliyor)
          const items = response.Items.map((item) => ({
            label: item.Text,
            href: item.Url || null, // Son item için null olabilir
          }));
          setBreadcrumbItems(items);
        } else {
          // API'den veri gelmezse boş bırak (breadcrumb gösterilmez)
          setBreadcrumbItems([]);
        }
      } catch {
        // Hata durumunda boş bırak (breadcrumb gösterilmez)
        setBreadcrumbItems([]);
      }
    };

    fetchBreadcrumb();
  }, [hotelName, locale, initialBreadcrumbItems]);

  useEffect(() => {
    if (!navRef.current) return;

    const navObserver = new IntersectionObserver(
      ([entry]) => {
        if (isInitialScrollInProgress.current) return;
        setShowSticky(!entry.isIntersecting);
      },
      {
        rootMargin: "-80px 0px 0px 0px",
        root: null,
        threshold: 0,
      },
    );

    navObserver.observe(navRef.current);

    return () => navObserver.disconnect();
  }, [isMobile]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSectionTrackingReady(true);
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!sectionTrackingReady) return;

    const sectionIds = ["overview", "rooms", "features", "policies", "info"];
    const headerOffset = 148;

    let ticking = false;

    const handler = () => {
      if (isInitialScrollInProgress.current) return;
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        if (isInitialScrollInProgress.current) {
          ticking = false;
          return;
        }
        let current = sectionIds[0];
        let bestDist = Number.POSITIVE_INFINITY;

        sectionIds.forEach((id) => {
          const el = document.getElementById(id);
          if (!el) return;
          const top = el.getBoundingClientRect().top;
          const dist = Math.abs(top - headerOffset);

          // headerOffset'a en yakın section aktif
          if (top - headerOffset <= 60 && dist < bestDist) {
            bestDist = dist;
            current = id;
          }
        });

        setActiveSection(current);
        ticking = false;
      });
    };

    window.addEventListener("scroll", handler, { passive: true });
    handler(); // initial: 2sn sonrasında mevcut scroll konumuna göre aktif tab
    return () => window.removeEventListener("scroll", handler);
  }, [sectionTrackingReady]);

  useEffect(() => {
    const shouldScrollToRooms =
      searchParams.get(ROOM_SEARCH_SCROLL_PARAM) === "1";
    if (!shouldScrollToRooms) return;

    const timeoutId = setTimeout(() => {
      const roomsSection = document.getElementById("rooms");
      if (!roomsSection) return;

      isInitialScrollInProgress.current = true;
      setActiveSection("rooms");
      smoothScrollToElement(roomsSection, 120, 0);

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete(ROOM_SEARCH_SCROLL_PARAM);
      window.history.replaceState({}, "", currentUrl.toString());

      setTimeout(() => {
        isInitialScrollInProgress.current = false;
      }, 300);
    }, 120);

    return () => clearTimeout(timeoutId);
  }, [searchParams]);

  const renderCheapestRoomButtonDesktop = useCallback(() => {
    // Sadece "Odalar" sekmesinin altına inildikten sonra (yani
    // features/policies/info bölümlerindeyken) göster.
    const shouldShowForSection =
      activeSection === "features" ||
      activeSection === "policies" ||
      activeSection === "info";

    if (
      !shouldShowForSection ||
      isMobile ||
      !roomsData ||
      roomsData.length === 0
    ) {
      return null;
    }

    // roomsData'dan en ucuz oda seçeneğini bul
    const allRoomOptions = roomsData.flatMap((roomType) =>
      (roomType.RoomPrices || []).map((price) => ({
        roomId: roomType.RoomId,
        discountedPrice:
          price.RoomPricing?.NetPrice || price.RoomPricing?.GrossPrice || 0,
        priceKey: price.PriceKey,
      })),
    );

    if (allRoomOptions.length === 0) {
      return null;
    }

    const cheapestRoomOption = allRoomOptions.sort(
      (a, b) => a.discountedPrice - b.discountedPrice,
    )[0];

    return (
      <div className="flex w-full flex-row items-center justify-end gap-4 max-lg:hidden">
        <span className="text-foreground-default text-[23px] leading-[30px] font-semibold break-words">
          {formatPrice(cheapestRoomOption.discountedPrice, currency, 0)}
        </span>

        <Button
          onClick={() => {
            const target = document.getElementById(
              `room_${cheapestRoomOption.roomId}`,
            );

            if (target) {
              smoothScrollToElement(target, 120, 1800);
            }
          }}
        >
          {t("hotel.bookRoom")}
        </Button>
      </div>
    );
  }, [activeSection, roomsData, isMobile, currency, t, formatPrice]);

  // Error durumunda popup göster
  useEffect(() => {
    if (!error) {
      return;
    }

    const defaultConfig = {
      isBottomSheet: true, // Mobile'da bottom sheet, desktop'ta normal popup
      size: "md",
      showCloseButton: true,
      closeOnBackdropClick: true,
      closeOnEscape: true,
      showBackdrop: true,
      hideTitleBorder: true,
      title: (
        <FontAwesomeIcon
          fontSize={28}
          className="text-warning h-7! w-7!"
          icon={byPrefixAndName.far["circle-exclamation"]}
        />
      ),
      className: "sm:h-[270px] sm:w-[409px] sm:max-h-[270px] sm:max-w-[409px]",
      headerClassName: "!items-start pt-5! pb-0! sm:px-6",
      titleWrapperClassName: "!left-4 !translate-x-0",
      titleClassName:
        "w-[54px] h-[54px] bg-warning-weak flex items-center justify-center rounded-full",
      contentClassName:
        "pt-4! scrollbar-hide flex-1 overflow-y-auto p-6 md:px-6 md:pb-5",
    };

    try {
      openModal(HOTEL_NOT_FOUND_MODAL_ID, defaultConfig);
    } catch {}
  }, [error, isMobile, openModal]);

  const renderCheapestRoomButtonMobile = useCallback(() => {
    if (!roomsData || roomsData.length === 0) {
      return null;
    }

    // roomsData'dan en ucuz oda seçeneğini bul
    const allRoomOptions = roomsData.flatMap((roomType) =>
      (roomType.RoomPrices || []).map((price) => ({
        roomId: roomType.RoomId,
        discountedPrice:
          price.RoomPricing?.NetPrice || price.RoomPricing?.GrossPrice || 0,
        priceKey: price.PriceKey,
      })),
    );

    if (allRoomOptions.length === 0) {
      return null;
    }

    const cheapestRoomOption = allRoomOptions.sort(
      (a, b) => a.discountedPrice - b.discountedPrice,
    )[0];

    const nights =
      checkinDate && checkOutDate
        ? Math.ceil(
            (new Date(checkOutDate) - new Date(checkinDate)) /
              (1000 * 60 * 60 * 24),
          )
        : 0;

    return createPortal(
      <div className="fixed right-0 bottom-0 z-[11] flex w-full items-center justify-between border-t border-solid border-[#D0D0DC] bg-white p-4">
        <div className="flex flex-col items-start">
          <span className="text-sm text-(--color-primary-muted-subtext)">
            {nights > 0 ? t("hotel.nights", { count: nights }) : ""},{" "}
            {adult || 1} {t("hotel.adult")}
          </span>

          <span className="text-foreground-default text-[20px] leading-[30px] font-semibold break-words">
            {formatPrice(cheapestRoomOption.discountedPrice, currency, 0)}
          </span>
        </div>

        <Button
          onClick={() => {
            const target = document.getElementById(
              `room_${cheapestRoomOption.roomId}`,
            );

            if (target) {
              smoothScrollToElement(target, 120, 0);
            }
          }}
        >
          {t("hotel.chooseRoom")}

          <FontAwesomeIcon
            className="h-6 w-6"
            icon={byPrefixAndName.far["chevron-right"]}
          />
        </Button>
      </div>,
      document.body,
    );
  }, [roomsData, currency, t, checkinDate, checkOutDate, adult, formatPrice]);

  return (
    <main className="">
      {showSticky && (
        <div
          className={clsx(
            "border-border-weak z-[11] w-full border-b border-solid bg-white",
            isMobile
              ? "fixed top-[57px] h-[47px]"
              : "sticky top-[93px] h-[62px]",
          )}
        >
          <div className={clsx("h-full", !isMobile && "container mx-auto")}>
            <div className="flex h-full flex-row items-center justify-between">
              <div className="flex h-full items-end max-md:w-full">
                <TabNavigation
                  activeSection={activeSection}
                  onChangeActiveSection={setActiveSection}
                  className="border-none max-md:w-full [&_button]:pb-[10px] lg:[&_button]:pb-[15px]"
                />
              </div>

              {renderCheapestRoomButtonDesktop()}
            </div>
          </div>
        </div>
      )}

      {activeSection !== "rooms" &&
        isMobile &&
        renderCheapestRoomButtonMobile()}

      <section className="hotel-detail-wrapper mb-4 flex flex-col gap-[6px] sm:mb-10 md:gap-6">
        <div className="order-2 md:hidden">
          <TabNavigation
            activeSection={activeSection}
            onChangeActiveSection={setActiveSection}
            ref={isMobile ? navRef : undefined}
          />
        </div>
        <section className="order-2 container mt-2 md:order-1 md:mt-4 md:mb-0 xl:mb-4">
          <BreadCrumb items={breadcrumbItems} />
        </section>

        <div className="order-2 container mt-1 md:order-1 md:mt-0">
          <Header
            hotelData={hotelData}
            checkinDate={checkinDate}
            checkOutDate={checkOutDate}
            roomsData={roomsData}
            guests={guests}
            productForAnalytics={productForViewItem}
            passengersForAnalytics={passengersForAnalytics}
          />
        </div>
        <div id="overview" className="order-1 md:order-2 md:container">
          <GallerySection
            hotelData={hotelData}
            productForAnalytics={productForViewItem}
            passengersForAnalytics={passengersForAnalytics}
            checkinDate={checkinDate}
            checkOutDate={checkOutDate}
            roomsData={roomsData}
            guests={guests}
          />
        </div>
        <div className="order-3 container mt-[10px] flex min-w-0 gap-8 sm:mt-4 md:mt-0">
          <div className="flex min-w-0 flex-1 flex-col gap-8">
            <div className="hidden md:block">
              <TabNavigation
                activeSection={activeSection}
                onChangeActiveSection={setActiveSection}
                ref={!isMobile ? navRef : undefined}
              />
            </div>
            <div className="flex min-w-0 flex-col gap-5 md:flex-row md:justify-between md:gap-8">
              <div className="min-w-0 flex-1">
                <HotelFeaturesSection hotelData={hotelData} />
              </div>
              <div className="min-w-0 md:max-w-[294px] md:min-w-[294px] md:flex-shrink-0 md:basis-[294px]">
                <HotelInfoSection
                  hotelData={hotelData}
                  onMapViewOpen={trackHotelMapView}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-[var(--color-background-global)]">
        <div id="rooms" className="container py-4 sm:py-6 md:!py-12">
          <HotelRooms
            checkinDate={new Date(checkinDate)}
            checkoutDate={new Date(checkOutDate)}
            hotelSlug={hotelName}
            guests={guests}
            roomsData={roomsData}
            providerId={hotelData?.ProviderId}
            reference={hotelData?.Reference}
            normalSearchReference={hotelData?.NormalSearchReference}
            onAddToCart={handleRoomAddToCart}
          />
        </div>
      </section>

      <section id="features" className="container py-4 sm:py-6 md:!py-12">
        <HotelFeatures hotelData={hotelData} />
      </section>

      {(() => {
        // AboutHotel için içerik kontrolü
        const descriptions = hotelData?.Descriptions || [];
        const locationDesc = descriptions.find(
          (d) => d.Category === "Location",
        )?.Content;
        const roomsDesc = descriptions.find(
          (d) => d.Category === "Rooms",
        )?.Content;
        const businessDesc = descriptions.find(
          (d) => d.Category === "Business Amenities",
        )?.Content;
        const diningDesc = descriptions.find(
          (d) => d.Category === "Dining",
        )?.Content;
        const amenitiesDesc = descriptions.find(
          (d) => d.Category === "Amenities",
        )?.Content;
        const summary = hotelData?.Summary || hotelData?.Description;
        const hasAboutContent =
          locationDesc ||
          roomsDesc ||
          businessDesc ||
          diningDesc ||
          amenitiesDesc ||
          summary;

        if (!hasAboutContent) {
          return null;
        }

        return (
          <section className="bg-[var(--color-background-global)]">
            <div className="container py-4 sm:py-6 md:!py-12">
              <AboutHotel hotelData={hotelData} />
            </div>
          </section>
        );
      })()}

      <section id="policies" className="container py-6 md:py-12">
        <HotelPolicies hotelData={hotelData} />
      </section>

      <section className="container">
        <MoreAboutHotel hotelData={hotelData} />
      </section>

      <section className="container">
        <NearByHotels hotelData={hotelData} />
      </section>

      <section id="info" className="bg-[var(--color-background-global)]">
        <div className="container">
          <ImportantInfo hotelData={hotelData} />
        </div>
      </section>

      {/* Error popup - Search sayfasındaki ile aynı */}
      {Boolean(error) && (
        <Modal
          id={HOTEL_NOT_FOUND_MODAL_ID}
          iconWrapperClassName="hidden"
          onClose={handleHotelNotFoundModalClose}
        >
          <div className="flex flex-col gap-6 bg-white">
            <div className="">
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-[10px]">
                  <p className="text-foreground text-xl leading-26 font-semibold">
                    {t("hotel.noRoomFound")}
                  </p>
                  <p className="text-foreground-muted text-base leading-21">
                    {t("hotel.noRoomFoundDescription")}
                  </p>
                </div>
              </div>
            </div>
            <div className="pb-6">
              <Button
                onClick={handleHotelNotFoundModalClose}
                className="h-[50px] w-full rounded-lg text-center text-base font-medium"
              >
                {t("hotel.ok")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </main>
  );
}
